/**
 * Axios instance for orval-generated API clients
 * Integrates with existing apiService pattern
 */
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { APIM_GATEWAY_URLS, CURRENT_ENVIRONMENT } from "../config/env";
import { triggerGlobalLogout } from "../utils/globalLogout";
import {
  triggerForbiddenToast,
  triggerServerErrorToast,
  triggerConnectionErrorToast,
  triggerRateLimitToast,
} from "../utils/globalToast";

// Token getter function - will be set by Authenticated component
type TokenGetter = () => Promise<string>;
let getTokenFn: TokenGetter | null = null;

/**
 * Set the token getter function from WorkOS useAuth hook
 * This should be called once during app initialization in Authenticated.jsx
 */
export const setAxiosTokenGetter = (fn: TokenGetter): void => {
  getTokenFn = fn;
};

// Create axios instance with base configuration
const axiosInstance = axios.create({
  baseURL: APIM_GATEWAY_URLS[CURRENT_ENVIRONMENT],
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token from WorkOS
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get fresh token from WorkOS SDK (handles refresh automatically)
    if (getTokenFn) {
      try {
        const token = await getTokenFn();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Failed to get access token for axios request:", error);
      }
    } else {
      console.warn(
        "⚠️ Axios token getter not initialized - requests may fail with 401",
      );
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor - handle errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // Handle different error types (matches existing apiService pattern)
    switch (status) {
      case 401:
        triggerGlobalLogout();
        break;
      case 403:
        triggerForbiddenToast();
        break;
      case 429:
        triggerRateLimitToast();
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        triggerServerErrorToast();
        break;
      default:
        if (!error.response) {
          triggerConnectionErrorToast();
        }
    }

    return Promise.reject(error);
  },
);

/**
 * Custom instance for orval
 * Matches the signature expected by orval-generated code
 */
export const customInstance = <T>(
  config: AxiosRequestConfig,
): Promise<AxiosResponse<T>> => {
  return axiosInstance.request<T>(config);
};

export default customInstance;
